import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AuthService } from '@klickdata/core/auth';
import { FormHelper } from '@klickdata/core/form';
import { MediaComponent } from '@klickdata/core/media/src/media.component';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { AppScope, Resource, ResourceService, ResourceTag, ResourceTypes } from '@klickdata/core/resource';
import { ResourceItem, ResourceItemService, ResourceItemTypes } from '@klickdata/core/resource-item';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';

import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { TagService } from '@klickdata/core/resource/src/tag/tag.service';
import { Utils } from '@klickdata/core/util';
import { MaterialParentDisplayModeBottomSheetComponent } from '@klickdata/shared-components/src/materials-manager-core/materials-manager-parent/material-parent-display-mode-bottom-sheet/material-parent-display-mode-bottom-sheet.component';
import { ResourceListingSheetComponent } from '@klickdata/shared-components/src/resource-listing-sheet/resource-listing-sheet.component';
import { ResourceBuilderService } from '../../resource-builder.service';
import { MatDialog } from '@angular/material/dialog';
import { ResourceItemDirective } from '../resource-item.directive';
import { ResourceMediaTypes } from '@klickdata/core/question/src/question-type/question-type';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { MediaType } from '@klickdata/core/media/src/media-type';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { MessageService } from '@klickdata/core/message';
import { ResourceEditBottomSheetComponent } from '@klickdata/shared-components/src/resource-edit-bottom-sheet/resource-edit-bottom-sheet.component';

@Component({
    selector: 'app-resource-item-material-form',
    templateUrl: './resource-item-material-form.component.html',
    styleUrls: ['./resource-item-material-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceItemMaterialFormComponent
    extends ResourceItemDirective
    implements OnInit, OnDestroy, AfterViewInit
{
    @ViewChild('title') titleInput: ElementRef;
    @Input()
    public item: ResourceItem;
    public loadingUploader: Subject<boolean> = new Subject<boolean>();
    public ItemTypes = ResourceItemTypes;
    public destroy: Subject<boolean> = new Subject<boolean>();
    public displayDesc = false;
    public showCoverImg: boolean;
    public selection: number[] = [];
    public selectedResource$: Observable<Resource>;
    AppScope = AppScope;
    @ViewChild('app_media') mediaCompRef: MediaComponent;
    public resourceTags$: Observable<ResourceTag[]>;
    public displayedMaterialResourceItem$: BehaviorSubject<ResourceItem> = new BehaviorSubject<ResourceItem>(null);
    public resourceItems: ResourceItem[];
    public loadingSaveBundle: Subject<boolean> = new Subject<boolean>();
    public currentIndex = 0;
    Utils = Utils;
    constructor(
        protected builderService: ResourceBuilderService,
        protected changeRef: ChangeDetectorRef,
        protected tagService: TagService,
        protected itemService: ResourceItemService,
        protected mobileService: MobileService,
        protected resourceService: ResourceService,
        protected dialog: MatDialog,
        protected authService: AuthService,
        protected mediaService: MediaService,
        protected messageService: MessageService,
        protected bottomSheet: MatBottomSheet
    ) {
        super(resourceService, changeRef, dialog, authService, mobileService, bottomSheet);
    }

    ngOnInit() {
        this.builderService
            .getItems()
            .pipe(takeUntil(this.destroy))
            .subscribe((items) => (this.selection = items.map((item) => item.child_resource_id)));

        this.generateResourceItems();
        // this.onResourceExist
        //     .asObservable()
        //     .pipe(takeUntil(this.destroy))
        //     .subscribe((resource) => {
        //         this.selected = resource;
        //         this.changeRef.markForCheck();
        //     });
    }
    protected afterResourceSelect() {
        this.generateResourceItems();
    }

    private generateResourceItems() {
        this.itemService
            .getResourceItems(this.childGroup.value.child_resource_id)
            .pipe(
                shareReplay(),
                takeUntil(this.destroy),
                filter((items) => !!items?.length)
            )
            .subscribe((items) => {
                this.resourceItems = items;
                this.displayedMaterialResourceItem$.next(items[this.currentIndex]);
                this.changeRef.markForCheck();
            });
    }

    public addMedia(errorMsg?: string) {
        if (this.selected?.media_id) {
            this.showCoverImg = !this.showCoverImg;
            this.changeRef.markForCheck();
            return;
        }
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'add_photo_alternate',
                title: $localize`Add an image`,
                description: $localize`Please select your preferred way to add an image.`,
                type: this.selected?.type_id,
                errorMsg: errorMsg,
            },
        });
        this.getMediaTypeFromSlideIn();
    }
    private getMediaTypeFromSlideIn() {
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === this.selected?.type_id),
                takeUntil(this.destroy),
                filter((data) => data && data.value),
                switchMap((data) => {
                    this.loadingUploader.next(true);
                    if (data.value === ResourceMediaTypes.FILE) {
                        return of(Number(data.mediaId));
                    } else {
                        return this.initAIUpload(data);
                    }
                }),
                switchMap((mediaId) => {
                    this.selected.media_id = mediaId;
                    return this.resourceService.update({ id: this.selected.id, media_id: mediaId }, true);
                })
            )
            .subscribe((data) => {
                this.loadingUploader.next(false);
                this.changeRef.markForCheck();
            });
    }
    private initAIUpload(data: any): Observable<number> {
        return this.mediaService
            .uploadMediaByLink({
                prompt: data.aiText,
                type: MediaType.PROMPT,
                scope_id: ResourceTypes.scopeByChildType(this.selected.type_id),
            })
            .pipe(
                takeUntil(this.destroy),
                catchError((err) => {
                    this.handleAIRequestFailure(err);
                    return of(null);
                }),
                map((media) => Number(media.id))
            );
    }
    handleAIRequestFailure(err) {
        if (err && err.error && err.error.error) {
            this.messageService.openMessage(MessageErrorComponent, err.error.error.messages.join('/n'));
        }
    }
    updateResourceMedia(mediaId?: number) {
        this.resourceService
            .update({ id: this.selected.id, media_id: null }, true)
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                this.selected.media_id = mediaId ?? null;
                this.changeRef.markForCheck();
            });
    }
    removeImage() {
        this.selected.media_id = null;
        this.resourceService
            .update({ id: this.selected.id, media_id: null }, true)
            .pipe(takeUntil(this.destroy))
            .subscribe(() => this.changeRef.markForCheck());
    }
    ngAfterViewInit() {
        this.titleInput?.nativeElement?.focus();
    }
    editItem(item: ResourceItem) {}
    openItemOrganizer(bundleItem: ResourceItem) {
        if (!bundleItem.children_ids?.length) return;
        let childrenItems: ResourceItem[];

        this.itemService
            .getResourceItemsByParentItemId(bundleItem.id)
            .pipe(
                switchMap((items) => {
                    childrenItems = items;
                    return this.bottomSheet
                        .open(MaterialParentDisplayModeBottomSheetComponent, {
                            data: {
                                id: bundleItem.id,
                                resourceItems: items,
                            },
                        })
                        .afterDismissed();
                }),
                takeUntil(this.destroy),
                filter((result) => !!result),
                map((value) => {
                    this.loadingSaveBundle.next(true);
                    childrenItems.forEach((item) => {
                        const leftSideIndex = value.leftSideItems.findIndex((v) => v.id === item.id);
                        const rightSideIndex = value.rightSideItems.findIndex((v) => v.id === item.id);
                        const align =
                            leftSideIndex !== -1
                                ? 'left'
                                : rightSideIndex !== -1
                                ? 'right'
                                : this.getDefaultLayoutAlign(item.item_type_value);
                        const order =
                            leftSideIndex !== -1 ? leftSideIndex : rightSideIndex !== -1 ? rightSideIndex : null;
                        const matchedResItem = childrenItems.find((resItem) => resItem.id && resItem.id === item.id);
                        if (!!matchedResItem) {
                            if (!matchedResItem.metadata) {
                                matchedResItem.metadata = {};
                            }
                            matchedResItem.metadata.layout = {
                                align: align,
                                order: order,
                            };
                        }
                    });
                    return childrenItems.map((item) => ({ id: item.id, metadata: item.metadata }));
                }),
                switchMap((items) => {
                    return this.itemService.update({ id: bundleItem.id, children_items: items });
                })
            )
            .subscribe((item) => {
                this.resourceItems[this.currentIndex] = item;
                this.changeRef.markForCheck();
                this.displayedMaterialResourceItem$.next(item);
                this.loadingSaveBundle.next(false);
            });
    }
    private getDefaultLayoutAlign(itemType: string): string {
        if (itemType.indexOf('image') !== -1) return 'right';
        if (itemType.indexOf('video') !== -1) return 'right';
        if (itemType.indexOf('audio') !== -1) return 'right';
        return 'left';
    }
    // onResourceExist(resource: Resource) {
    //     this.updateItemGroup(resource);
    //     this.resourceTags$ = !resource.tags_attached
    //         ? EMPTY
    //         : !!resource.tags?.length
    //         ? of(resource.tags.map((tag) => new ResourceTag(tag)))
    //         : this.tagService.getResourceTags(resource.id);
    //     this.selectedResource$ = of(resource);
    // }

    updateChildGroup(resource: Resource): void {
        this.childGroup.patchValue({
            title: resource.title,
            bullets: resource.bullets,
        });

        this.childGroup.patchValue({
            instructions: resource.instructions,
        });
    }

    updateItemFromResource(resource: Resource, field: string) {
        if (field === 'title') {
            this.childGroup.get('title').patchValue(resource.title);
        } else if (field === 'bullets') {
            this.childGroup.get('bullets').patchValue(resource.bullets);
        }
        this.childGroup.markAsDirty();
        this.childGroup.updateValueAndValidity();
        this.changeRef.markForCheck();
    }

    ngOnDestroy(): void {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }

    public showTagResources(tag: ResourceTag) {
        this.bottomSheet.open(ResourceListingSheetComponent, {
            data: {
                id: tag.id,
                label: tag.name,
                scope_id: AppScope.TAGS,
            },
            panelClass: 'sheet-wrapper',
        });
    }
}
